import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import {
    DateInput,
    Select,
    MultiSelect,
    PageTitleWithToolTip,
    PrimaryButton,
    CommonModal,
    LoadingButtonV4 as LoadingButton,
    SecondaryButton, InputNumber
} from '../../Common';
import { useNavigate } from '../../../hooks';
import { isDesktop, isMobile } from '../../../utils';
import {
    Grid,
    Typography,
    IconButton,
    Paper,
    Box,
    Collapse,
    Alert
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import SystemSelect from './SystemSelect';
import Enums from "../../../enums";
import { postRequestHeaders } from "../../../utils";

const inspectionTypes = [{ id: 1, label: "Leak Inspection" }];
const systemTypeFilters = [
    { id: 31, name: "Comfort Cooling" },
    { id: 63, name: "Process Cooling" },
    { id: 22, name: "Refrigeration" }
];
const paperProps = { mt: 2, pb: 3, px: 2, borderRadius: "25px" };
const paperResponsiveStyle = {
    ...paperProps,
    mx: { xs: 2, md: 0 }
};
const containerResponsiveStyle = { px: { xs: 2, md: 0 } };
const requiredFields = [
    "inspectionDate",
    "leakLocationId",
    "leakSublocationId",
    "faultCauseTypeId",
];

const SiteLeakInspection = (props) => {
    const workOrderId = props.match.params.id;
    const [inspectionDate, setInspectionDate] = useState(null);
    const [leakInspectionMethod, setLeakInspectionMethod] = useState(null);
    const [primaryTechnician, setPrimaryTechnician] = useState(null);
    const [systemTypes, setSystemTypes] = useState([
        Enums.SE.AssetType.ComfortCooling,
        Enums.SE.AssetType.ProcessCooling,
        Enums.SE.AssetType.Refrigeration
    ]);
    const [minimumChargeCapacity, setMinimumChargeCapacity] = useState('');
    const [assets, setAssets] = useState([]);
    const [originalAssets, setOriginalAssets] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(true);
    const [dropDownOptions, setDropDownOptions] = useState({
        technicians: [],
        leakLocations: [],
        leakLocationCategories: [],
        leakDetectionMethods: [],
        leakCauses: []
    });
    const navigate = useNavigate({ pageName: 'SiteLeakInspection' });

    const fetchData = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakInspectionDropdown(workOrderId));
        const info = await response.json();
        const {
            leakLocations,
            leakLocationCategories,
            leakDetectionMethods,
            leakCauses,
            siteLeak_Technicians,
            coolingAppliance
        } = info;
        // console.log('API response', info);

        const technicians = siteLeak_Technicians.filter((obj) => obj.validCertificateNumber === true);
        const assets = coolingAppliance.map(({ asset }) => ({ ...asset, workItemDetail: {} }));
        
        setAssets(assets);
        setOriginalAssets(assets);
        setDropDownOptions({
            technicians,
            leakLocations,
            leakLocationCategories,
            leakDetectionMethods,
            leakCauses
        });
    };
    
    const getFilteredData = () => {
        let originalAssetsClone = [...originalAssets].map(a => ({ ...a, isChecked: false }));
        
        if (systemTypes.length < systemTypeFilters.length) {
            originalAssetsClone = originalAssetsClone.filter(a => systemTypes.includes(a.assetType.id));
        }

        if (minimumChargeCapacity) {
            originalAssetsClone = originalAssetsClone.filter(a =>
                a.coolingAppliance.gasChargeCapacityLbs >= parseFloat(minimumChargeCapacity)
            );
        }
        
        setAssets(originalAssetsClone);
    };
    
    const isSaveDisabled = () => {
        // Inspection Details - required fields
        if (!inspectionDate || !leakInspectionMethod || !primaryTechnician) {
            return true;
        }

        // at least 1 included asset
        const includedAssets = assets.filter(a => a.isIncludedInLeakInspection);
        if (includedAssets.length === 0) {
            return true;
        }
        
        const savedDisabled = assets
            .filter(a => a.isChecked)
            .map((a) => {
                const { isChecked, isIncludedInLeakInspection, workItemDetail, workItemDetail: { wasLeakFound } } = a;
    
                // asset checked - isIncludedInLeakInspection = true - wasLeakFound = false
                if (isChecked && isIncludedInLeakInspection && !wasLeakFound) {
                    return false;
                }
    
                // asset checked
                // isIncludedInLeakInspection = true
                // wasLeakFound = true
                // all 4 requiredFields answered
                const allRequiredFieldsAnswered = requiredFields.every(field => !!workItemDetail[field]);
                if (
                    isChecked
                    && isIncludedInLeakInspection
                    && wasLeakFound
                    && allRequiredFieldsAnswered
                ) {
                    return false;
                }
                
                return true;
            });
        
        // saveDisabled[i] = assets[i]
        // if saveDisabled[i] = false, assets[i] is not ready for submission
        return savedDisabled.includes(true);
    };
    
    const handleSelectAll = () => {
        const checkedAssets = assets.map((a) =>
            ({
                ...a,
                isChecked: !a.isChecked &&
                    a.coolingAppliance.coolingApplianceStatusId !== Enums.SE.CoolingApplianceStatus.UnderRepair
            })
        );
        return setAssets(checkedAssets);
    };
    
    const handleDeselectAll = () => {
        // delete wasLeakFound && isIncludedInLeakInspection when deselecting all assets
        const sanitizedAssets = assets.map((a) => {
            const { wasLeakFound, isIncludedInLeakInspection, isChecked, workItemDetail, ...rest } = a;
            return { ...rest };
        });
        return setAssets(sanitizedAssets);
    };
    
    const handleMarkAsNoLeak = () => {
        const selectedAssets = [...assets].map(a => ({
            ...a,
            ...(a.isChecked && {
                isIncludedInLeakInspection: true,
                workItemDetail: { ...a.workItemDetail, wasLeakFound: false },
            })
        }));
        return setAssets(selectedAssets);
    };
    
    const handleInspectionDate = (newDate) => {
        setInspectionDate(newDate);
        const assetsClone = assets
            .map(a => ({
                ...a,
                workItemDetail: {
                    ...a.workItemDetail,
                    ...(a.workItemDetail.wasLeakFound && { inspectionDate: newDate })
                }
            }));
        setAssets(assetsClone);
    };
    
    // Function to map refactored UI state models to backend models
    // data = assets to save
    const formatSubmissionData = (data) => ({
        ID: workOrderId,
        InstanceId: UserInfo.instanceId,
        LeakDetectionMethodId: leakInspectionMethod,
        PrimaryTechnicianId: primaryTechnician,
        ServiceDate: inspectionDate,
        UserId: UserInfo.userId,
        WorkItemTypeId: 2,
        WorkItems: [...data]
    });
    
    const submit = async () => {
        setLoading(true);
        const assetsToSubmit = formatSubmissionData(assets.filter(a => a.isIncludedInLeakInspection));
        console.log('assetsToSubmit', assetsToSubmit)

        const response =  await fetch(Routes.ServiceEvent.AddSiteLeakInspection(), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify(assetsToSubmit)
        });

        if (response.status === 200) {
            setLoading(false);
            return setModalOpen(true);
        }

        setLoading(false);
        return toast.error('There was an error saving the asset', { duration: 6000 });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        getFilteredData();
    }, [systemTypes, minimumChargeCapacity]);

    return (
        <>
            <Grid
                container
                spacing={2}
                sx={{ 
                    mt: { xs: 2, md: 0 },
                    ...containerResponsiveStyle
                }}
            >
                <Grid item xs={"auto"}>
                    <IconButton
                        aria-label="close"
                        onClick={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={"auto"} md={6}>
                    <PageTitleWithToolTip  pageTitle={`Work Order #${workOrderId}`} />
                </Grid>
                <Grid item xs={12} md sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton
                        useLargeButton
                        buttonText="Complete"
                        fullWidth={!isDesktop}
                        disabled={isSaveDisabled()}
                        loading={loading}
                        onClick={submit}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Collapse in={alertOpen}>
                        <Alert severity="info" onClose={() => setAlertOpen(false)}>
                            It is recommended each leak location on an appliance is recorded as a separate leak ticket.
                        </Alert>
                    </Collapse>
                </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ ...paperResponsiveStyle }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} mt={2}>
                        <PageTitleWithToolTip pageTitle="Inspection Details" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            disabled
                            value={inspectionTypes[0].id}
                            label="Inspection Type"
                            variant="outlined"
                            options={inspectionTypes}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateInput
                            variant="outlined"
                            allowPast
                            allowFuture={false}
                            label="Inspection Date *"
                            date={inspectionDate}
                            onChange={handleInspectionDate}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={leakInspectionMethod}
                            label="Leak Inspection Method"
                            variant="outlined"
                            options={dropDownOptions.leakDetectionMethods}
                            onChange={(e) => setLeakInspectionMethod(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={primaryTechnician}
                            label="Primary Technician"
                            variant="outlined"
                            options={dropDownOptions.technicians}
                            onChange={(e) => setPrimaryTechnician(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ ...paperResponsiveStyle }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} mt={2}>
                        <PageTitleWithToolTip pageTitle="Filters" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MultiSelect
                            renderAllOption
                            value={systemTypes}
                            label="System Type"
                            variant="outlined"
                            options={systemTypeFilters}
                            onChange={(e) => setSystemTypes(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <InputNumber
                            fullWidth
                            hideStepper
                            allowDecimal
                            label="Charge Capacity (lbs)"
                            variant="outlined"
                            value={minimumChargeCapacity}
                            onChange={(e) => setMinimumChargeCapacity(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>
            
            <Grid container sx={{ ...containerResponsiveStyle }}>
                <Grid item xs={12} mt={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        {!assets.some(a => a.isChecked) ? (
                            <SecondaryButton
                                buttonText={"Select All"}
                                onClick={handleSelectAll}
                                disabled={loading}
                            />
                        ): (
                            <SecondaryButton
                                buttonText={isDesktop ? "Deselect All" : "Clear"}
                                onClick={handleDeselectAll}
                                disabled={loading}
                            />  
                        )}
                        {assets.some(a => a.isChecked) && (
                            <PrimaryButton
                                buttonText={"Mark Selected As No Leak"}
                                onClick={handleMarkAsNoLeak}
                                disabled={loading}
                            />  
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} mt={2}>
                    <Typography>
                        Select each system included in the inspection and identify whether leak found or no leak found
                    </Typography>
                </Grid>
            </Grid>

            <Grid
                container
                spacing={2}
                sx={{
                    mt: 2,
                    mb: { xs: 2, md: 0 },
                }}
            >
                {assets.map((asset, index) => (
                    <Grid item xs={12} key={index}>
                        <SystemSelect
                            index={index}
                            asset={asset}
                            assets={assets}
                            setAssets={setAssets}
                            dropDownOptions={dropDownOptions}
                            loading={loading}
                            topLevelInspectionDate={inspectionDate}
                        />
                    </Grid>
                ))}
            </Grid>

            <CommonModal
                open={modalOpen}
                close={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
                header={"Site Leak Inspection created."}
                headerTopMargin={isMobile && 8}
            >
                <Grid container>
                    <Grid item xs={12} align='center'>
                        <img
                            style={{ paddingTop: '1%', paddingBottom: '5%' }}
                            src={'/images/Asset_Create_Img.svg'}
                            width="300px"
                            height="300px"
                            alt={"Site Leak Inspection successfully created."}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <Typography
                            variant='h5'
                            sx={{
                                px: { xs: 2, md: 0 },
                                py: 2,
                                fontSize: "1.15em",
                                fontWeight: 400,
                                color: 'black'
                            }}
                        >
                            {"If any leaks were repaired during this visit, remember to log them in a Leak Repair service record."}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center' sx={{ pb: { xs: 2, md: 0 } }}>
                        <PrimaryButton
                            width={70}
                            useLargeButton
                            buttonText='VIEW WORK ORDER'
                            onClick={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
                        />
                    </Grid>
                </Grid>
            </CommonModal>
        </>
    );
};

export default SiteLeakInspection;